import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import api from '../services/api'
import Reveal from '../components/landing/Reveal'
import CinematicNav from '../components/landing/CinematicNav'
import CinematicFooter from '../components/landing/CinematicFooter'
import LoadingSpinner from '../components/LoadingSpinner'

const SITE = 'https://synergymultimediaacademy.com'

export default function CreativeToolsPage() {
  const [catalog, setCatalog] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    let cancelled = false
    api.get('/creative-tools')
      .then(({ data }) => { if (!cancelled) setCatalog(data) })
      .catch(() => { if (!cancelled) setError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const featured = catalog?.featured
  const categories = catalog?.categories || []
  const commerce = catalog?.commerce || []

  return (
    <div className="cinema-page min-h-screen selection:bg-cinema-glow/30 selection:text-white">
      <Helmet>
        <title>Creative Tools — Synergy Multimedia Academy</title>
        <meta
          name="description"
          content="Professional creative software built by SMA for photography, video, graphics, and digital production. Offline-first when the work belongs on your machine."
        />
        <link rel="canonical" href={`${SITE}/creative-tools`} />
        <meta property="og:title" content="SMA Creative Tools" />
        <meta
          property="og:description"
          content="Tools for the work behind the work. Professional creative software built by SMA."
        />
        <meta property="og:url" content={`${SITE}/creative-tools`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} onNavigate={() => {}} />

      <header className="relative pt-28 pb-12 sm:pt-36 sm:pb-20 border-b border-white/10">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <Reveal>
            <p className="text-[0.7rem] uppercase tracking-[0.28em] text-cinema-gold font-sans font-semibold">
              SMA Creative Tools
            </p>
            <h1 className="mt-5 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl text-white font-light tracking-tight leading-[1.05]">
              Tools for the work behind the work.
            </h1>
            <p className="mt-6 max-w-2xl text-sm sm:text-base text-cinema-soft/85 leading-relaxed font-sans">
              Professional creative software built by SMA for the real work of photography, video, graphics and digital production.
            </p>
            <p className="mt-4 max-w-xl text-sm text-cinema-muted font-sans leading-relaxed">
              Offline-first when the work belongs on your machine. Connected when the cloud genuinely adds value.
            </p>
          </Reveal>

          <Reveal delay={80} className="mt-12 sm:mt-16 border-y border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="py-6 md:py-8 md:pr-8">
                <p className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-cinema-gold">01 / Built locally</p>
                <p className="mt-3 font-display text-2xl text-white font-light">On the machine</p>
                <p className="mt-2 text-xs text-cinema-muted font-sans leading-relaxed">
                  Heavy media work stays where the footage already is.
                </p>
              </div>
              <div className="py-6 md:py-8 md:px-8">
                <p className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-cinema-gold">02 / Connected intelligently</p>
                <p className="mt-3 font-display text-2xl text-white font-light">Only when it helps</p>
                <p className="mt-2 text-xs text-cinema-muted font-sans leading-relaxed">
                  Online services are used when they add something the machine cannot.
                </p>
              </div>
              <div className="py-6 md:py-8 md:pl-8">
                <p className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-cinema-gold">03 / From SMA</p>
                <p className="mt-3 font-display text-2xl text-white font-light">Learn, then do</p>
                <p className="mt-2 text-xs text-cinema-muted font-sans leading-relaxed">
                  SMA teaches the work, and builds software for doing it.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </header>

      {loading ? (
        <div className="py-24 flex justify-center"><LoadingSpinner /></div>
      ) : error ? (
        <section className="py-24">
          <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
            <p className="text-xs uppercase tracking-[0.2em] text-cinema-gold font-sans">Catalog unavailable</p>
            <h2 className="mt-4 font-display text-3xl text-white font-light">The Creative Tools catalog could not be loaded.</h2>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="cinema-btn-ghost mt-8"
            >
              Try again
            </button>
          </div>
        </section>
      ) : (
        <>
          {featured && (
            <section className="border-b border-white/10" aria-labelledby="featured-tool">
              <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
                <Reveal>
                  <div className="flex flex-wrap items-baseline justify-between gap-4">
                    <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">
                      Featured · {featured.category_name}
                    </p>
                    <p className="text-[0.65rem] uppercase tracking-[0.22em] text-cinema-muted font-sans">
                      {featured.status_label}
                    </p>
                  </div>
                  <h2 id="featured-tool" className="mt-6 font-display text-4xl sm:text-6xl text-white font-light tracking-tight">
                    {featured.name}
                  </h2>
                  <p className="mt-4 max-w-3xl font-display text-xl sm:text-2xl text-cinema-soft font-light">
                    {featured.headline}
                  </p>
                </Reveal>

                <Reveal delay={60} className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
                  <div className="lg:col-span-7">
                    <p className="text-sm sm:text-base text-cinema-soft/85 leading-relaxed font-sans">
                      {featured.description}
                    </p>
                    {featured.explore_path && (
                      <Link to={featured.explore_path} className="cinema-btn-text mt-8 text-cinema-gold">
                        Explore {featured.name}
                        <span aria-hidden="true">→</span>
                      </Link>
                    )}
                  </div>
                  <ul className="lg:col-span-5 border-t border-white/10">
                    {featured.highlights.map((item) => (
                      <li key={item} className="py-3 border-b border-white/10 text-sm text-white font-sans">
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </section>
          )}

          <section className="border-b border-white/10" aria-labelledby="tool-catalog">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Catalog</p>
                <h2 id="tool-catalog" className="mt-4 font-display text-3xl sm:text-5xl text-white font-light tracking-tight">
                  The tools, by discipline.
                </h2>
              </Reveal>

              <div className="mt-12">
                {categories.map((category, index) => (
                  <article key={category.code} className="border-t border-white/10 py-8 sm:py-10">
                    <div className="grid lg:grid-cols-12 gap-6 lg:gap-12">
                      <div className="lg:col-span-3">
                        <p className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-cinema-gold">
                          {String(index + 1).padStart(2, '0')}
                        </p>
                        <h3 className="mt-2 font-display text-2xl text-white font-light">{category.name}</h3>
                      </div>
                      <div className="lg:col-span-9">
                        {category.tools.length === 0 ? (
                          <p className="text-sm text-cinema-muted font-sans">{category.forthcoming_note}</p>
                        ) : (
                          <ul>
                            {category.tools.map((tool) => (
                              <li key={tool.slug} className="border-b border-white/10 py-4 first:pt-0">
                                <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                                  <div>
                                    {tool.explore_path ? (
                                      <Link to={tool.explore_path} className="font-display text-xl text-white hover:text-cinema-gold transition-colors">
                                        {tool.name}
                                      </Link>
                                    ) : (
                                      <p className="font-display text-xl text-white/80">{tool.name}</p>
                                    )}
                                    <p className="mt-1 text-sm text-cinema-muted font-sans">{tool.short_description}</p>
                                  </div>
                                  <p className={`text-[0.65rem] uppercase tracking-[0.18em] font-sans shrink-0 ${
                                    tool.listing_tone === 'forthcoming' ? 'text-cinema-muted' : 'text-cinema-gold'
                                  }`}>
                                    {tool.status_label}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section aria-labelledby="choose-how">
            <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-14 sm:py-20">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.22em] text-cinema-gold font-sans font-semibold">Access</p>
                <h2 id="choose-how" className="mt-4 font-display text-3xl sm:text-5xl text-white font-light tracking-tight">
                  Choose how you work.
                </h2>
                <p className="mt-4 max-w-2xl text-sm text-cinema-muted font-sans leading-relaxed">
                  Prices are not published yet. Each way of working will open when the tool it belongs to is ready.
                </p>
              </Reveal>
              <div className="mt-12 border-t border-white/10">
                {commerce.map((model, index) => (
                  <div key={model.code} className="grid md:grid-cols-12 gap-4 md:gap-8 py-8 border-b border-white/10">
                    <p className="md:col-span-1 text-[0.65rem] font-mono uppercase tracking-[0.2em] text-cinema-gold">
                      {String(index + 1).padStart(2, '0')}
                    </p>
                    <div className="md:col-span-4">
                      <h3 className="font-display text-2xl text-white font-light">{model.name}</h3>
                      <p className="mt-2 text-[0.65rem] uppercase tracking-[0.18em] text-cinema-muted font-sans">
                        {model.availability_label}
                      </p>
                    </div>
                    <div className="md:col-span-7">
                      <p className="text-sm text-cinema-soft font-sans">{model.audience}</p>
                      <p className="mt-2 text-sm text-cinema-muted font-sans leading-relaxed">{model.summary}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
    </div>
  )
}
